import { useMemo } from 'react';
import type { MapSection } from '../lib/api';
import { sectionTypeColor, SECTION_TYPE_GROUPS, SECTION_TYPE_COLORS } from '../lib/sections';
import {
  STADIUM_CELLS,
  STADIUM_DECOR,
  CELL_BY_ID,
  MAP_W,
  MAP_H,
  MAP_CX,
  MAP_CY,
  FIELD,
  LEVEL_FILL,
  describeSection,
} from '../lib/metlife';

/**
 * MetLife Stadium section map. Geometry and section inventory live in
 * lib/metlife.ts and mirror the official Giants seating chart — real section
 * numbers (including 111A/220C-style splits), clockwise numbering, west end
 * zone on the left, Giants sideline on top.
 */

/** Availability keyed by map cell, tolerant of seller-entered variants. */
export type ResolvedSection = MapSection & {
  /** The section string as stored in D1 — use this for listing queries. */
  querySection: string;
};

export function resolveSections(sections: MapSection[]): Map<string, ResolvedSection> {
  const m = new Map<string, ResolvedSection>();
  const put = (cellId: string, s: MapSection) => {
    const prev = m.get(cellId);
    if (!prev) {
      m.set(cellId, { ...s, querySection: s.section });
      return;
    }
    m.set(cellId, {
      ...prev,
      listing_count: prev.listing_count + s.listing_count,
      min_price_cents: Math.min(prev.min_price_cents, s.min_price_cents),
    });
  };
  for (const s of sections) {
    const key = s.section.trim().toUpperCase();
    if (CELL_BY_ID.has(key)) {
      put(key, s);
      continue;
    }
    // Fallback: a seller entered e.g. "205" — light up 205A rather than nothing.
    const base = key.replace(/[^0-9]/g, '');
    if (!base) continue;
    const match = STADIUM_CELLS.find((c) => c.id.replace(/[^0-9]/g, '') === base);
    if (match) put(match.id, s);
  }
  return m;
}

/** Suite bands, club strips, corner suites, and gate stubs from the chart. */
function Decor({ dimmed }: { dimmed?: boolean }) {
  return (
    <g opacity={dimmed ? 0.35 : 1} pointerEvents="none">
      {STADIUM_DECOR.gates.map((p, i) => (
        <path key={`gate-${i}`} d={p} fill="#6b7280" />
      ))}
      {STADIUM_DECOR.bands.map((b, i) => (
        <g key={`band-${i}`}>
          <path d={b.path} fill={b.fill} />
          {b.label && b.labelPos && (
            <text
              x={b.labelPos[0]}
              y={b.labelPos[1]}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={b.labelSize ?? 8}
              fontWeight={700}
              letterSpacing={0.8}
              fill="#ffffff"
              opacity={0.92}
            >
              {b.label}
            </text>
          )}
          {b.endLabels?.map((e) => (
            <text
              key={e.text}
              x={e.pos[0]}
              y={e.pos[1]}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={7.5}
              fontWeight={700}
              fill="#ffffff"
              opacity={0.92}
            >
              {e.text}
            </text>
          ))}
        </g>
      ))}
      {STADIUM_DECOR.suites.map((s) => (
        <g key={s.id}>
          <path d={s.path} fill="#3050b5" />
          <text
            x={s.label[0]}
            y={s.label[1] - 6}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={8}
            fontWeight={600}
            letterSpacing={1}
            fill="#ffffff"
            opacity={0.85}
          >
            SUITE
          </text>
          <text
            x={s.label[0]}
            y={s.label[1] + 6}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={s.labelSize}
            fontWeight={700}
            fill="#ffffff"
          >
            {s.id}
          </text>
        </g>
      ))}
    </g>
  );
}

function Field() {
  const ez = FIELD.w / 12; // 10-yd end zones on a 120-yd field
  const yardLines = Array.from({ length: 11 }, (_, i) => FIELD.x + ez * (i + 1));
  return (
    <g>
      <rect
        x={FIELD.x}
        y={FIELD.y}
        width={FIELD.w}
        height={FIELD.h}
        rx={8}
        fill="#15803d"
        stroke="#166534"
        strokeWidth={2}
      />
      {/* End zones */}
      <rect x={FIELD.x} y={FIELD.y} width={ez} height={FIELD.h} rx={8} fill="#0b2265" />
      <rect x={FIELD.x + FIELD.w - ez} y={FIELD.y} width={ez} height={FIELD.h} rx={8} fill="#0b2265" />
      <text
        x={FIELD.x + ez / 2}
        y={MAP_CY}
        transform={`rotate(-90 ${FIELD.x + ez / 2} ${MAP_CY})`}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={800}
        letterSpacing={2}
        fill="#ffffff"
      >
        GIANTS
      </text>
      <text
        x={FIELD.x + FIELD.w - ez / 2}
        y={MAP_CY}
        transform={`rotate(90 ${FIELD.x + FIELD.w - ez / 2} ${MAP_CY})`}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={11}
        fontWeight={800}
        letterSpacing={2}
        fill="#ffffff"
      >
        GIANTS
      </text>
      {yardLines.map((x) => (
        <line key={x} x1={x} y1={FIELD.y + 4} x2={x} y2={FIELD.y + FIELD.h - 4} stroke="#ffffff" strokeWidth={1} opacity={0.7} />
      ))}
      {/* Sideline tabs */}
      <g>
        <rect x={MAP_CX - 52} y={FIELD.y - 26} width={104} height={19} rx={9.5} fill="#0b2265" />
        <text x={MAP_CX} y={FIELD.y - 13} textAnchor="middle" fontSize={10.5} fontWeight={700} fill="#ffffff">
          GIANTS
        </text>
        <rect
          x={MAP_CX - 52}
          y={FIELD.y + FIELD.h + 7}
          width={104}
          height={19}
          rx={9.5}
          fill="#ffffff"
          stroke="#9ca3af"
        />
        <text
          x={MAP_CX}
          y={FIELD.y + FIELD.h + 20}
          textAnchor="middle"
          fontSize={10.5}
          fontWeight={700}
          fill="#374151"
        >
          VISITORS
        </text>
      </g>
    </g>
  );
}

export default function StadiumMap({
  sections,
  onSectionClick,
  selectedSection,
}: {
  sections: MapSection[];
  onSectionClick: (cellId: string, querySection: string) => void;
  selectedSection?: string | null;
}) {
  const resolved = useMemo(() => resolveSections(sections), [sections]);

  return (
    <div style={{ marginTop: '1rem' }}>
      <p className="muted">
        Sections with active listings are coloured by seat type — hover for prices, click any
        section for a closer look.
      </p>
      <svg
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        width="100%"
        style={{ maxHeight: '75vh', background: '#f3f4f6', borderRadius: 12 }}
        role="img"
        aria-label="MetLife Stadium section map"
      >
        <Field />
        <Decor />
        {STADIUM_CELLS.map((cell) => {
          const data = resolved.get(cell.id);
          const active = !!data;
          const selected = selectedSection === cell.id;
          return (
            <g key={cell.id}>
              <path
                className={`map-cell${active ? ' active' : ''}`}
                d={cell.path}
                fill={active ? sectionTypeColor(data.section_type) : LEVEL_FILL[cell.level]}
                stroke={selected ? '#a71930' : '#f3f4f6'}
                strokeWidth={selected ? 2.5 : 1.2}
                onClick={() => onSectionClick(cell.id, data?.querySection ?? cell.id)}
              >
                <title>
                  {`Section ${cell.id} · ${describeSection(cell.id)}` +
                    (active
                      ? ` — ${data.listing_count} listing${data.listing_count === 1 ? '' : 's'} from $${(
                          data.min_price_cents / 100
                        ).toFixed(0)}/seat`
                      : ' — no active listings')}
                </title>
              </path>
              <text
                x={cell.label[0]}
                y={cell.label[1]}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={cell.labelSize}
                fontWeight={active ? 700 : 500}
                fill={active ? '#ffffff' : '#5b657a'}
                pointerEvents="none"
                transform={
                  cell.labelRotate
                    ? `rotate(${cell.labelRotate.toFixed(1)} ${cell.label[0].toFixed(1)} ${cell.label[1].toFixed(1)})`
                    : undefined
                }
              >
                {cell.id}
              </text>
            </g>
          );
        })}
      </svg>

      <Legend sections={sections} />
    </div>
  );
}

/**
 * Zoomed-in view of one section in context — used by the section drill-down.
 * Same shared geometry as the full map, cropped to the section's neighbours.
 */
export function SectionZoom({ sectionId }: { sectionId: string }) {
  const cell = CELL_BY_ID.get(sectionId.trim().toUpperCase());
  if (!cell) return null;
  const pad = Math.max(cell.bbox.w, cell.bbox.h) * 0.85;
  const vb = `${(cell.bbox.x - pad).toFixed(1)} ${(cell.bbox.y - pad).toFixed(1)} ${(
    cell.bbox.w + pad * 2
  ).toFixed(1)} ${(cell.bbox.h + pad * 2).toFixed(1)}`;
  return (
    <svg
      viewBox={vb}
      width="100%"
      style={{ background: '#f3f4f6', borderRadius: 8 }}
      role="img"
      aria-label={`Section ${cell.id} location detail`}
    >
      <Field />
      <Decor dimmed />
      {STADIUM_CELLS.map((c) => {
        const isTarget = c.id === cell.id;
        return (
          <g key={c.id}>
            <path
              d={c.path}
              fill={isTarget ? '#0b2265' : '#dfe4ee'}
              stroke={isTarget ? '#a71930' : '#f3f4f6'}
              strokeWidth={isTarget ? 1.6 : 1}
            />
            <text
              x={c.label[0]}
              y={c.label[1]}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={c.labelSize}
              fontWeight={isTarget ? 800 : 500}
              fill={isTarget ? '#ffffff' : '#8a93a6'}
              pointerEvents="none"
              transform={
                c.labelRotate
                  ? `rotate(${c.labelRotate.toFixed(1)} ${c.label[0].toFixed(1)} ${c.label[1].toFixed(1)})`
                  : undefined
              }
            >
              {c.id}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Legend: dims section types that have no active listings for this game. */
function Legend({ sections }: { sections: MapSection[] }) {
  const present = useMemo(
    () => new Set(sections.map((s) => s.section_type).filter(Boolean) as string[]),
    [sections]
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '0.75rem' }}>
      {SECTION_TYPE_GROUPS.map((group) => (
        <div key={group.level}>
          <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            {group.level}
          </div>
          {group.types.map((t) => (
            <div
              key={t}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.78rem',
                opacity: present.has(t) ? 1 : 0.4,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: SECTION_TYPE_COLORS[t],
                  display: 'inline-block',
                }}
              />
              {t}
            </div>
          ))}
        </div>
      ))}
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: '0.25rem' }}>
          Availability
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem' }}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: LEVEL_FILL.lower,
              display: 'inline-block',
            }}
          />
          No active listings
        </div>
      </div>
    </div>
  );
}
